import type TriggerEngine from "../../TriggerEngine";
import type { EventSourceResult } from "../../TriggerEngine.interfaces";
import type { ITriggerRule } from "../../../interfaces/Widgets";
import { ruleIdentity } from "../ruleIdentity";
import { MAX_TIMEOUT_MS, parseScheduledTime } from "../timing";

export const wireScheduledEvents = (engine: TriggerEngine): EventSourceResult => {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const entries = new Map<string, { rule: ITriggerRule; identity: string; deadline: number }>();
  let disposed = false;

  const cancel = (ruleId: string) => {
    clearTimeout(timers.get(ruleId));
    timers.delete(ruleId);
    entries.delete(ruleId);
  };
  const schedule = (entry: { rule: ITriggerRule; identity: string; deadline: number }) => {
    const ruleId = entry.rule.id;
    const tick = () => {
      if (disposed || entries.get(ruleId) !== entry) return;
      timers.delete(ruleId);
      const remaining = entry.deadline - Date.now();
      if (remaining > 0) {
        timers.set(ruleId, setTimeout(tick, Math.min(MAX_TIMEOUT_MS, remaining)));
      } else {
        void engine.fireRule(entry.rule, () => !disposed && entries.get(ruleId) === entry);
      }
    };
    timers.set(ruleId, setTimeout(tick, Math.min(MAX_TIMEOUT_MS, entry.deadline - Date.now())));
  };
  const sync = (rules: ITriggerRule[]) => {
    const enabled = new Map(rules.filter((rule) => rule.enabled && rule.event === "scheduled")
      .map((rule) => [rule.id, rule]));
    for (const ruleId of entries.keys()) {
      if (!enabled.has(ruleId)) cancel(ruleId);
    }
    for (const [ruleId, rule] of enabled) {
      const identity = ruleIdentity(rule);
      if (entries.get(ruleId)?.identity === identity) continue;
      cancel(ruleId);
      const deadline = parseScheduledTime(rule.eventConfig ?? {});
      if (deadline === null || deadline <= Date.now()) continue;
      const entry = { rule, identity, deadline };
      entries.set(ruleId, entry);
      schedule(entry);
    }
  };
  return {
    sync,
    timers,
    unsubscribers: [() => {
      disposed = true;
      for (const ruleId of entries.keys()) cancel(ruleId);
    }],
  };
};
